import type { AIInterviewType } from '@ispot/shared'

import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const INTERVIEW_TYPES: { value: AIInterviewType; label: string }[] = [
  { value: 'user_interview', label: 'User Interview' },
  { value: 'buyer_interview', label: 'Buyer Interview' },
  { value: 'customer_interview', label: 'Customer Interview' },
  { value: 'stakeholder_interview', label: 'Stakeholder Interview' },
  { value: 'discovery_meeting', label: 'Discovery Meeting' },
  { value: 'validation_meeting', label: 'Validation Meeting' },
]

export interface PlanFormValues {
  researchGoal: string
  interviewType: AIInterviewType
  focusTopics: string
  description: string
  welcomeMessage: string
  consentText: string
  collectName: boolean
  collectEmail: boolean
  estimatedDuration: string
  maxResponses: string
}

interface PlanMetadataFormProps {
  values: PlanFormValues
  onChange: <K extends keyof PlanFormValues>(key: K, value: PlanFormValues[K]) => void
}

function Field({ label, optional, hint, className, children }: {
  label: string; optional?: boolean; hint?: string; className?: string; children: React.ReactNode
}) {
  return (
    <div className={className}>
      <label className="mb-1 block text-body font-medium text-fg">
        {label} {optional && <span className="font-normal text-fg-muted">(optional)</span>}
      </label>
      {hint && <p className="mb-1 text-caption text-fg-muted">{hint}</p>}
      {children}
    </div>
  )
}

export function PlanMetadataForm({ values, onChange }: PlanMetadataFormProps) {
  return (
    <div className="w-3/5 space-y-4 overflow-y-auto border-r border-border p-5 max-lg:w-full max-lg:border-r-0">
      <Field label="Research Goal">
        <Textarea value={values.researchGoal} onChange={(e) => onChange('researchGoal', e.target.value)} rows={3} />
      </Field>
      <Field label="Interview Type">
        <Select value={values.interviewType} onValueChange={(v) => onChange('interviewType', v as AIInterviewType)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {INTERVIEW_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Focus Topics" hint="Comma-separated">
        <Input value={values.focusTopics} onChange={(e) => onChange('focusTopics', e.target.value)} />
      </Field>
      <Field label="Description" optional>
        <Textarea value={values.description} onChange={(e) => onChange('description', e.target.value)} rows={2} />
      </Field>
      <Field label="Welcome Message" optional hint="Shown on landing page">
        <Textarea value={values.welcomeMessage} onChange={(e) => onChange('welcomeMessage', e.target.value)} rows={2} />
      </Field>
      <Field label="Consent Text" optional hint="Shown before starting">
        <Textarea value={values.consentText} onChange={(e) => onChange('consentText', e.target.value)} rows={2} />
      </Field>
      <div className="flex gap-6">
        <label className="flex items-center gap-2 text-body text-fg">
          <Checkbox checked={values.collectName} onCheckedChange={(v) => onChange('collectName', !!v)} />
          Collect Name
        </label>
        <label className="flex items-center gap-2 text-body text-fg">
          <Checkbox checked={values.collectEmail} onCheckedChange={(v) => onChange('collectEmail', !!v)} />
          Collect Email
        </label>
      </div>
      <div className="flex gap-4">
        <Field label="Estimated Duration (min)" className="flex-1">
          <Input type="number" value={values.estimatedDuration} onChange={(e) => onChange('estimatedDuration', e.target.value)} />
        </Field>
        <Field label="Max Responses" optional className="flex-1">
          <Input type="number" value={values.maxResponses} onChange={(e) => onChange('maxResponses', e.target.value)} />
        </Field>
      </div>
    </div>
  )
}
